import { __assign } from "tslib";
import isNumber from 'lodash/isNumber';
import { GraphNGLegendEventMode } from './types';
import { FieldType, formattedValueToString, getFieldColorModeForField, getFieldDisplayName, getFieldSeriesColor, outerJoinDataFrames, } from '@grafana/data';
import { UPlotConfigBuilder } from '../uPlot/config/UPlotConfigBuilder';
import { FIXED_UNIT } from './GraphNG';
import { AxisPlacement, DrawStyle, PointVisibility, ScaleDirection, ScaleOrientation, } from '../uPlot/config';
var defaultFormatter = function (v) { return (v == null ? '-' : v.toFixed(1)); };
var defaultConfig = {
    drawStyle: DrawStyle.Line,
    showPoints: PointVisibility.Auto,
    axisPlacement: AxisPlacement.Auto,
};
export function mapMouseEventToMode(event) {
    if (event.ctrlKey || event.metaKey || event.shiftKey) {
        return GraphNGLegendEventMode.AppendToSelection;
    }
    return GraphNGLegendEventMode.ToggleSelection;
}
export function preparePlotFrame(data, dimFields) {
    return outerJoinDataFrames({
        frames: data,
        joinBy: dimFields.x,
        keep: dimFields.y,
        keepOriginIndices: true,
    });
}
export function preparePlotConfigBuilder(frame, theme, getTimeRange, getTimeZone) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
    var builder = new UPlotConfigBuilder(getTimeZone);
    // X is the first field in the aligned frame
    var xField = frame.fields[0];
    var seriesIndex = 0;
    if (xField.type === FieldType.time) {
        builder.addScale({
            scaleKey: 'x',
            orientation: ScaleOrientation.Horizontal,
            direction: ScaleDirection.Right,
            isTime: true,
            range: function () {
                var r = getTimeRange();
                return [r.from.valueOf(), r.to.valueOf()];
            },
        });
        builder.addAxis({
            scaleKey: 'x',
            isTime: true,
            placement: AxisPlacement.Bottom,
            timeZone: getTimeZone(),
            theme: theme,
        });
    }
    else {
        // Not time!
        builder.addScale({
            scaleKey: 'x',
            orientation: ScaleOrientation.Horizontal,
            direction: ScaleDirection.Right,
        });
        builder.addAxis({
            scaleKey: 'x',
            placement: AxisPlacement.Bottom,
            theme: theme,
        });
    }
    var indexByName = undefined;
    var _loop_1 = function (i) {
        var field = frame.fields[i];
        var config = field.config;
        var customConfig = __assign(__assign({}, defaultConfig), config.custom);
        if (field === xField || field.type !== FieldType.number) {
            return "continue";
        }
        field.state.seriesIndex = seriesIndex++;
        var fmt = (_a = field.display) !== null && _a !== void 0 ? _a : defaultFormatter;
        var scaleKey = config.unit || FIXED_UNIT;
        var colorMode = getFieldColorModeForField(field);
        var scaleColor = getFieldSeriesColor(field, theme);
        var seriesColor = scaleColor.color;
        // The builder will manage unique scaleKeys and combine where appropriate
        builder.addScale({
            scaleKey: scaleKey,
            orientation: ScaleOrientation.Vertical,
            direction: ScaleDirection.Up,
            distribution: (_b = customConfig.scaleDistribution) === null || _b === void 0 ? void 0 : _b.type,
            log: (_c = customConfig.scaleDistribution) === null || _c === void 0 ? void 0 : _c.log,
            min: field.config.min,
            max: field.config.max,
            softMin: customConfig.axisSoftMin,
            softMax: customConfig.axisSoftMax,
        });
        if (customConfig.axisPlacement !== AxisPlacement.Hidden) {
            builder.addAxis({
                scaleKey: scaleKey,
                label: customConfig.axisLabel,
                size: customConfig.axisWidth,
                placement: (_d = customConfig.axisPlacement) !== null && _d !== void 0 ? _d : AxisPlacement.Auto,
                formatValue: function (v) { return formattedValueToString(fmt(v)); },
                theme: theme,
            });
        }
        var showPoints = customConfig.drawStyle === DrawStyle.Points ? PointVisibility.Always : customConfig.showPoints;
        var fillOpacity = customConfig.fillOpacity;
        if (customConfig.fillBelowTo) {
            if (!indexByName) {
                indexByName = getNamesToFieldIndex(frame);
            }
            var t = indexByName.get(getFieldDisplayName(field, frame));
            var b = indexByName.get(customConfig.fillBelowTo);
            if (isNumber(b) && isNumber(t)) {
                builder.addBand({
                    series: [t, b],
                    fill: null,
                });
            }
            if (!fillOpacity) {
                fillOpacity = 35; // default from flot
            }
        }
        builder.addSeries({
            scaleKey: scaleKey,
            showPoints: showPoints,
            colorMode: colorMode,
            fillOpacity: fillOpacity,
            theme: theme,
            drawStyle: customConfig.drawStyle,
            lineColor: (_e = customConfig.lineColor) !== null && _e !== void 0 ? _e : seriesColor,
            lineWidth: customConfig.lineWidth,
            lineInterpolation: customConfig.lineInterpolation,
            lineStyle: customConfig.lineStyle,
            barAlignment: customConfig.barAlignment,
            pointSize: customConfig.pointSize,
            pointColor: (_f = customConfig.pointColor) !== null && _f !== void 0 ? _f : seriesColor,
            spanNulls: customConfig.spanNulls || false,
            show: !((_g = customConfig.hideFrom) === null || _g === void 0 ? void 0 : _g.graph),
            gradientMode: customConfig.gradientMode,
            thresholds: config.thresholds,
            // The following properties are not used in the uPlot config, but are utilized as transport for legend config
            dataFrameFieldIndex: (_h = field.state) === null || _h === void 0 ? void 0 : _h.origin,
            fieldName: getFieldDisplayName(field, frame),
            hideInLegend: (_j = customConfig.hideFrom) === null || _j === void 0 ? void 0 : _j.legend,
        });
    };
    for (var i = 0; i < frame.fields.length; i++) {
        _loop_1(i);
    }
    return builder;
}
export function getNamesToFieldIndex(frame) {
    var names = new Map();
    for (var i = 0; i < frame.fields.length; i++) {
        names.set(getFieldDisplayName(frame.fields[i], frame), i);
    }
    return names;
}
//# sourceMappingURL=utils.js.map